import type { PlaybackMode } from './types';

export const DEFAULT_ITEM_DURATION_MS = 10_000;

/**
 * Minimal structural view of a zone binding for duration resolution. Fits both
 * the player bundle's zones (kind 'media'/'url'/'text' + mimeType) and
 * admin-side zone data (asset kind 'video'/'image'/...).
 */
export interface DurationZone {
  kind?: string;
  mimeType?: string | null;
  durationMs?: number | null;
}

export type DurationZones =
  | ReadonlyArray<DurationZone | null | undefined>
  | Readonly<Record<string, DurationZone | null | undefined>>
  | null
  | undefined;

function isVideoZone(zone: DurationZone): boolean {
  if (typeof zone.mimeType === 'string') return zone.mimeType.startsWith('video/');
  if (zone.kind === 'video') return true;
  if (zone.kind && zone.kind !== 'media') return false;
  // No usable discriminator: only probed videos ever carry a positive durationMs.
  return true;
}

/** Max positive natural durationMs among the item's bound video zones, or null. */
export function naturalDurationMs(zones: DurationZones): number | null {
  if (!zones) return null;
  const list = Array.isArray(zones) ? zones : Object.values(zones);
  let max: number | null = null;
  for (const zone of list) {
    if (!zone || !isVideoZone(zone)) continue;
    const d = zone.durationMs;
    if (typeof d === 'number' && d > 0 && (max === null || d > max)) max = d;
  }
  return max;
}

export function effectiveDurationMs(
  item: { durationMs: number | null },
  zones?: DurationZones,
): number {
  if (item.durationMs && item.durationMs > 0) return item.durationMs;
  return naturalDurationMs(zones) ?? DEFAULT_ITEM_DURATION_MS;
}

/**
 * Picks the next item in a playlist according to its playback mode.
 * `previousIndex` is -1 to mean "no current".
 *
 * Accepts any shape with `items: { weight: number }[]` + `playbackMode`,
 * so callers can pass their app-specific playlist type directly without
 * adapting it to RendererPlaylist first.
 */
export function nextItemIndex(
  playlist: { items: ReadonlyArray<{ weight: number }>; playbackMode: PlaybackMode },
  previousIndex: number,
): number {
  if (playlist.items.length === 0) return -1;
  if (playlist.playbackMode === 'sequential') {
    return (previousIndex + 1) % playlist.items.length;
  }
  const total = playlist.items.reduce((sum, it) => sum + Math.max(0, it.weight), 0);
  if (total <= 0) return (previousIndex + 1) % playlist.items.length;
  let r = Math.random() * total;
  for (let i = 0; i < playlist.items.length; i++) {
    const w = Math.max(0, playlist.items[i]!.weight);
    if (r < w) return i;
    r -= w;
  }
  return playlist.items.length - 1;
}
